import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ShoppingCart,
  Plus,
  Search,
  Trash2,
  Send,
  Printer,
  Mail,
  Package,
  AlertTriangle,
  Check,
  ArrowRight,
  ArrowLeft,
  X,
  Building2,
  FileText,
  ClipboardList,
  MessageCircle,
  PlusCircle,
  Edit2,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { ProductDetailModal, defaultProduct } from "@/components/products";
import { DirectOrderLineDialog } from "@/components/direct/DirectOrderLineDialog";
import { OrderImportanceBadge } from "@/components/products/OrderImportanceBadge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PurchaseOrders = () => {
  // View state: 'list' or 'create'
  const [viewMode, setViewMode] = useState('list');
  
  // PO List state
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [showPODetail, setShowPODetail] = useState(false);
  // PO list search query — filters loaded POs by PO number, supplier name,
  // supplier number, status, items[].name and items[].plu_code.
  const [poSearchQuery, setPoSearchQuery] = useState("");
  
  // Screen state: 'setup' -> 'items' -> 'complete'
  const [currentScreen, setCurrentScreen] = useState('setup');
  
  // Screen 1: Setup
  const [orderType, setOrderType] = useState('STANDARD');
  const [orderMethod, setOrderMethod] = useState('MANUAL');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierResults, setSupplierResults] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [searchingSupplier, setSearchingSupplier] = useState(false);
  
  // Screen 2: Items
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [orderItems, setOrderItems] = useState([]);
  const [importanceMap, setImportanceMap] = useState({});
  const orderItemIdsKey = [...new Set(orderItems.map((i) => i.product_id).filter(Boolean))].sort().join(",");
  useEffect(() => {
    if (!orderItemIdsKey) return;
    axios.post(`${API}/products/order-importance/lookup`, orderItemIdsKey.split(","))
      .then((r) => setImportanceMap(r.data))
      .catch(() => {});
  }, [orderItemIdsKey]);
  const [autoSuggestItems, setAutoSuggestItems] = useState([]);
  const [loadingAutoSuggest, setLoadingAutoSuggest] = useState(false);
  
  // Product add modal
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productQty, setProductQty] = useState('');
  const [productCost, setProductCost] = useState('');
  const [stockWarning, setStockWarning] = useState(null);
  const [caseWarning, setCaseWarning] = useState(null);
  const [validatingProduct, setValidatingProduct] = useState(false);
  
  // Screen 3: Complete
  const [createdOrder, setCreatedOrder] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [emailAddress, setEmailAddress] = useState('bosveldbh@gmail.com');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Email PO from list
  const [emailingPOId, setEmailingPOId] = useState(null);
  const [deletingPO, setDeletingPO] = useState(null); // PO object pending delete confirmation
  const [deleteInFlight, setDeleteInFlight] = useState(false);

  const handleDeletePO = async () => {
    if (!deletingPO) return;
    setDeleteInFlight(true);
    try {
      const res = await axios.delete(`${API}/purchase-orders/${deletingPO.id}`);
      toast.success(`PO ${res.data.po_number || deletingPO.po_number} deleted`);
      setDeletingPO(null);
      fetchPurchaseOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete PO");
    } finally {
      setDeleteInFlight(false);
    }
  };
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailPOTarget, setEmailPOTarget] = useState(null);
  const [listEmailAddress, setListEmailAddress] = useState('bosveldbh@gmail.com');
  const [supplierEmail, setSupplierEmail] = useState('');
  const [supplierAutoEmail, setSupplierAutoEmail] = useState(false);
  const [emailToMyself, setEmailToMyself] = useState(false);
  const [myEmail] = useState('bosveldbh@gmail.com'); // Your email address
  
  // Edit PO state
  const [editingPO, setEditingPO] = useState(false);
  const [editPOItems, setEditPOItems] = useState([]);
  const [editProductSearch, setEditProductSearch] = useState('');
  const [editProductResults, setEditProductResults] = useState([]);
  const [searchingEditProducts, setSearchingEditProducts] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Refs
  const productSearchRef = useRef(null);
  const qtyInputRef = useRef(null);
  
  // Minimum order warning state
  const [showMinOrderWarning, setShowMinOrderWarning] = useState(false);
  const [minOrderDetails, setMinOrderDetails] = useState(null);
  
  // Add New Product Modal state
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [newProductData, setNewProductData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Edit-Existing-Product Modal state — lets the operator open a product's
  // full detail modal from inside the order builder without navigating away.
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loadingEditProduct, setLoadingEditProduct] = useState(false);

  const openEditProduct = async (productIdOrPlu) => {
    if (!productIdOrPlu) return;
    setLoadingEditProduct(true);
    try {
      // Fetch by id first (products in the order carry product_id), then
      // fall back to PLU if the id lookup 404s.
      let resp;
      try {
        resp = await axios.get(`${API}/products/${productIdOrPlu}`);
      } catch {
        resp = await axios.get(`${API}/products/paginated?limit=1&skip=0&search=${encodeURIComponent(productIdOrPlu)}`);
        const list = resp.data?.products || [];
        if (!list.length) throw new Error("Product not found");
        resp = { data: list[0] };
      }
      setEditingProduct(resp.data);
      setShowEditProductModal(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open product");
    } finally {
      setLoadingEditProduct(false);
    }
  };

  const handleSaveEditedProduct = async (productData) => {
    try {
      const pid = productData.id || editingProduct?.id;
      if (!pid) throw new Error("Missing product id");
      const resp = await axios.put(`${API}/products/${pid}`, productData);
      const updated = resp.data || productData;
      toast.success(`"${updated.name}" updated`);
      setShowEditProductModal(false);
      setEditingProduct(null);
      // Splice the freshly-saved values into any matching order row so
      // Order Qty stays put but Name / Cost / VAT / Case reflect the edit.
      setOrderItems(prev => prev.map(row => {
        if (row.product_id !== pid) return row;
        const newCost = Number(
          updated.last_cost_price_excl_vat
          ?? updated.average_cost_price_excl_vat
          ?? updated.cost_price
          ?? row.unit_cost
        );
        const qty = Number(row.quantity_ordered) || 0;
        return {
          ...row,
          name: updated.name ?? row.name,
          barcode: updated.barcode ?? row.barcode,
          supplier_plu: updated.supplier_plu ?? row.supplier_plu,
          case_size: updated.case_size ?? row.case_size,
          unit: updated.unit ?? row.unit,
          vat_category: updated.vat_category ?? row.vat_category,
          unit_cost: newCost,
          total_cost: Math.round(qty * newCost * 100) / 100,
        };
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || errorMessage?.(error) || "Update failed");
    }
  };

  // Once-Off Product (Direct Stock Order) state
  const [onceOffDialogOpen, setOnceOffDialogOpen] = useState(false);
  const [directOrderProduct, setDirectOrderProduct] = useState(null);
  const [onceOffName, setOnceOffName] = useState('');
  const [onceOffQty, setOnceOffQty] = useState('1');
  const [onceOffPrice, setOnceOffPrice] = useState('');
  const [onceOffAddToStock, setOnceOffAddToStock] = useState(false);
  const [savingOnceOff, setSavingOnceOff] = useState(false);
  
  // Order types
  const ORDER_TYPES = [
    { value: 'STANDARD', label: 'Standard Stock Order', description: 'Regular stock replenishment' },
    { value: 'DIRECT', label: 'Direct Stock Order', description: 'Direct delivery to customer' }
  ];
  
  const ORDER_METHODS = [
    { value: 'MANUAL', label: 'Manual Order', description: 'Search and add products manually' },
    { value: 'AUTO', label: 'Auto Suggest Order', description: 'System suggests items below reorder level' }
  ];

  // Fetch all purchase orders
  const fetchPurchaseOrders = async () => {
    setLoadingOrders(true);
    try {
      const response = await axios.get(`${API}/purchase-orders`);
      // Sort by created_at descending (newest first)
      const sorted = (response.data || []).sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setPurchaseOrders(sorted);
    } catch (error) {
      console.error("Error fetching purchase orders:", error);
      toast.error("Failed to load purchase orders");
    } finally {
      setLoadingOrders(false);
    }
  };

  // Fetch categories and departments for Add Product modal
  const fetchCategoriesDepartments = async () => {
    try {
      const response = await axios.get(`${API}/products/categories-departments`);
      setCategories(response.data.categories || []);
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Handle opening Add Product modal
  const handleOpenAddProduct = () => {
    fetchCategoriesDepartments();
    setNewProductData({
      ...defaultProduct,
      barcodes: [],
      id: null,
      supplier: selectedSupplier?.name || "",
      supplier_id: selectedSupplier?.id || "",
    });
    setShowAddProductModal(true);
  };

  // Handle saving new product
  const handleSaveNewProduct = async (productData) => {
    try {
      const response = await axios.post(`${API}/products`, productData);
      toast.success(`Product "${productData.name}" created successfully!`);
      setShowAddProductModal(false);
      
      // Auto-add the new product to the order if we have supplier selected
      if (selectedSupplier) {
        const newProduct = response.data;
        handleSelectProduct({
          ...newProduct,
          id: newProduct.id,
          plu_code: newProduct.plu_code,
          name: newProduct.name,
          current_stock: newProduct.current_stock || 0,
          last_cost_price_excl_vat: newProduct.last_cost_price_excl_vat || newProduct.cost_price || 0,
        });
      }
    } catch (error) {
      console.error("Error creating product:", error);
      toast.error(error.response?.data?.detail || "Failed to create product");
    }
  };

  // Load POs on mount and when returning to list view
  useEffect(() => {
    if (viewMode === 'list') {
      fetchPurchaseOrders();
    }
  }, [viewMode]);

  // View PO details
  const handleViewPO = async (po) => {
    try {
      const response = await axios.get(`${API}/purchase-orders/${po.id}`);
      setSelectedPO(response.data);
      setShowPODetail(true);
      setEditingPO(false);
    } catch (error) {
      console.error("Error fetching PO details:", error);
      toast.error("Failed to load PO details");
    }
  };

  // Start editing PO
  const handleStartEditPO = () => {
    if (selectedPO) {
      setEditPOItems(selectedPO.items?.map(item => ({
        ...item,
        quantity: item.quantity_ordered || item.quantity || 0
      })) || []);
      setEditingPO(true);
    }
  };

  // Cancel editing PO
  const handleCancelEditPO = () => {
    setEditingPO(false);
    setEditPOItems([]);
    setEditProductSearch('');
    setEditProductResults([]);
  };

  // Reactivate an EXPIRED purchase order
  const handleReactivatePO = async () => {
    if (!selectedPO) return;
    try {
      await axios.post(`${API}/purchase-orders/${selectedPO.id}/reactivate`);
      toast.success(`Order ${selectedPO.po_number} reactivated`);
      setShowPODetail(false);
      fetchPurchaseOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reactivate order");
    }
  };

  // Update item quantity in edit mode
  const handleEditItemQty = (index, newQty) => {
    const qty = parseInt(newQty) || 0;
    setEditPOItems(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        quantity: qty,
        quantity_ordered: qty,
        total_cost: qty * (updated[index].unit_cost || 0)
      };
      return updated;
    });
  };

  // Update item cost in edit mode
  const handleEditItemCost = (index, newCost) => {
    const cost = parseFloat(newCost) || 0;
    setEditPOItems(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        unit_cost: cost,
        total_cost: (updated[index].quantity || updated[index].quantity_ordered || 0) * cost
      };
      return updated;
    });
  };

  // Remove item in edit mode
  const handleRemoveEditItem = (index) => {
    setEditPOItems(prev => prev.filter((_, i) => i !== index));
  };

  // Add product to edit items
  const handleAddEditProduct = (product) => {
    const existingIndex = editPOItems.findIndex(item => item.product_id === product.id);
    if (existingIndex >= 0) {
      // Increment quantity if already exists
      handleEditItemQty(existingIndex, (editPOItems[existingIndex].quantity || 0) + 1);
    } else {
      // Add new item
      const unitCost = +(((product.last_cost_price_excl_vat || product.cost_price || 0) + (product.transport_cost || 0)).toFixed(2));
      setEditPOItems(prev => [...prev, {
        product_id: product.id,
        plu_code: product.plu_code,
        supplier_plu: product.supplier_plu,
        name: product.name,
        quantity: 1,
        quantity_ordered: 1,
        unit_cost: unitCost,
        total_cost: unitCost,
        vat_category: product.vat_category || 'Standard 15%',
        transport_cost: product.transport_cost || 0
      }]);
    }
    setEditProductSearch('');
    setEditProductResults([]);
  };

  // Search products for edit mode
  useEffect(() => {
    const searchEditProducts = async () => {
      if (editProductSearch.length < 1) {
        setEditProductResults([]);
        return;
      }
      
      setSearchingEditProducts(true);
      try {
        const response = await axios.get(`${API}/products/search?q=${encodeURIComponent(editProductSearch)}`);
        setEditProductResults(response.data.slice(0, 10));
      } catch (error) {
        console.error("Error searching products:", error);
      } finally {
        setSearchingEditProducts(false);
      }
    };
    
    const debounce = setTimeout(searchEditProducts, 300);
    return () => clearTimeout(debounce);
  }, [editProductSearch]);

  // Save edited PO
  const handleSaveEditPO = async () => {
    if (editPOItems.length === 0) {
      toast.error("Order must have at least one item");
      return;
    }

    setSavingEdit(true);
    try {
      const subtotal = editPOItems.reduce((sum, item) => sum + (item.total_cost || 0), 0);
      // SARS-compliant: skip VAT for zero-rated / exempt items
      const vatAmount = editPOItems.reduce((sum, item) => {
        const vatCategory = (item.vat_category || 'Standard 15%').toString().toLowerCase();
        if (vatCategory.includes('zero') || vatCategory.includes('exempt') || vatCategory.includes('0%')) {
          return sum;
        }
        return sum + ((item.total_cost || 0) * 0.15);
      }, 0);
      const total = subtotal + vatAmount;

      const updateData = {
        items: editPOItems,
        subtotal,
        vat_amount: vatAmount,
        total
      };

      const response = await saveWithRetry(() => axios.put(`${API}/purchase-orders/${selectedPO.id}`, updateData));
      setSelectedPO(response.data);
      setEditingPO(false);
      setEditPOItems([]);
      toast.success("Purchase order updated successfully");
      fetchPurchaseOrders(); // Refresh list
    } catch (error) {
      console.error("Error updating PO:", error);
      toast.error(errorMessage(error, "Failed to update purchase order"));
    } finally {
      setSavingEdit(false);
    }
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT': return 'bg-gray-100 text-gray-700';
      case 'SENT': return 'bg-blue-100 text-blue-700';
      case 'PARTIALLY_RECEIVED': return 'bg-yellow-100 text-yellow-700';
      case 'RECEIVED': return 'bg-green-100 text-green-700';
      case 'CANCELLED': return 'bg-red-100 text-red-700';
      case 'EXPIRED': return 'bg-orange-100 text-orange-700 border border-orange-300';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Search suppliers
  useEffect(() => {
    const searchSuppliers = async () => {
      if (supplierSearch.length < 1) {
        setSupplierResults([]);
        return;
      }
      
      setSearchingSupplier(true);
      try {
        const response = await axios.get(`${API}/suppliers/search?q=${encodeURIComponent(supplierSearch)}`);
        setSupplierResults(response.data.slice(0, 10));
      } catch (error) {
        console.error("Error searching suppliers:", error);
      } finally {
        setSearchingSupplier(false);
      }
    };
    
    const debounce = setTimeout(searchSuppliers, 300);
    return () => clearTimeout(debounce);
  }, [supplierSearch]);

  // Search products as user types
  useEffect(() => {
    const searchProducts = async () => {
      if (productSearch.length < 1) {
        setProductResults([]);
        return;
      }
      
      setSearchingProducts(true);
      try {
        const response = await axios.get(`${API}/products/search?q=${encodeURIComponent(productSearch)}`);
        setProductResults(response.data.slice(0, 10));
      } catch (error) {
        console.error("Error searching products:", error);
      } finally {
        setSearchingProducts(false);
      }
    };
    
    const debounce = setTimeout(searchProducts, 300);
    return () => clearTimeout(debounce);
  }, [productSearch]);

  // State for no-allocation warning
  const [noAllocationWarning, setNoAllocationWarning] = useState(null);
  const [showAllSupplierItems, setShowAllSupplierItems] = useState(false);

  // Load auto-suggest items when supplier selected and method is AUTO
  useEffect(() => {
    const loadAutoSuggest = async () => {
      if (orderMethod === 'AUTO' && selectedSupplier && currentScreen === 'items') {
        setLoadingAutoSuggest(true);
        setNoAllocationWarning(null);
        try {
          const url = showAllSupplierItems 
            ? `${API}/purchase-orders/auto-suggest/${selectedSupplier.id}?show_all=true`
            : `${API}/purchase-orders/auto-suggest/${selectedSupplier.id}`;
          const response = await axios.get(url);
          const rawSuggestions = response.data.suggestions || [];
          // When the cashier is NOT viewing the "Show all supplier items" mode,
          // hide rows where stock_on_order already covers the shortfall
          // (suggested_qty <= 0). Those products don't need ordering right now —
          // showing them just clutters the auto-suggest list.
          const usableSuggestions = showAllSupplierItems
            ? rawSuggestions
            : rawSuggestions.filter((s) => Number(s.suggested_qty) > 0);
          setAutoSuggestItems(usableSuggestions);
          
          // Check if no products are allocated to this supplier
          if (response.data.message) {
            setNoAllocationWarning(response.data.message);
          } else if (response.data.products_linked_to_supplier === 0) {
            setNoAllocationWarning("No products are allocated to this supplier. Use 'Stock Allocation' to link products to suppliers first.");
          } else if (usableSuggestions.length === 0 && response.data.products_linked_to_supplier > 0 && !showAllSupplierItems) {
            // Distinguish two reasons for "no rows":
            // (a) nothing is below reorder
            // (b) everything below reorder is already covered by existing POs
            const coveredByOrderCount = rawSuggestions.filter((s) => s.covered_by_order || s.has_stock_on_order).length;
            if (coveredByOrderCount > 0) {
              setNoAllocationWarning(`${response.data.products_linked_to_supplier} products are linked to this supplier. ${coveredByOrderCount} item${coveredByOrderCount === 1 ? '' : 's'} below reorder level — but existing stock-on-order already covers the shortfall, so nothing needs to be ordered now.`);
            } else {
              setNoAllocationWarning(`${response.data.products_linked_to_supplier} products are linked to this supplier, but none need reordering (all stock levels are above reorder level).`);
            }
          }
          
          // Auto-add all suggested items to order
          const items = usableSuggestions.map(item => ({
            product_id: item.product_id,
            plu_code: item.plu_code,
            barcode: item.barcode,
            supplier_plu: item.supplier_plu || '',
            name: item.name,
            description: item.description,
            quantity_ordered: item.suggested_qty,
            unit_cost: item.unit_cost,
            total_cost: item.suggested_qty * item.unit_cost,
            current_stock: item.current_stock,
            min_level: item.min_level,
            reorder_level: item.reorder_level,
            max_level: item.max_level,
            stock_on_order: item.stock_on_order || 0,
            covered_by_order: item.covered_by_order || false,
            exceeds_max: false,
            sold_per_kg: item.sold_per_kg || false,
            case_size: item.case_size || null,
            pack_size: item.pack_size || null,
            six_month_sold: item.six_month_sold || 0,
            vat_category: item.vat_category || 'Standard 15%',
            vat_rate: item.vat_rate,
            transport_cost: item.transport_cost || 0
          }));
          setOrderItems(items);
        } catch (error) {
          console.error("Error loading auto-suggest items:", error);
          toast.error("Failed to load suggested items");
        } finally {
          setLoadingAutoSuggest(false);
        }
      }
    };
    
    loadAutoSuggest();
  }, [orderMethod, selectedSupplier, currentScreen, showAllSupplierItems]);

  const handleSelectSupplier = (supplier) => {
    setSelectedSupplier(supplier);
    setSupplierSearch(supplier.supplier_number + ' - ' + supplier.name);
    setSupplierResults([]);
  };

  const handleProceedToItems = () => {
    if (!selectedSupplier) {
      toast.error("Please select a supplier");
      return;
    }
    setCurrentScreen('items');
    setOrderItems([]);
    
    // Focus on product search after screen change
    setTimeout(() => {
      if (productSearchRef.current) {
        productSearchRef.current.focus();
      }
    }, 100);
  };

  // Select a product from dropdown and open modal
  const handleSelectProduct = async (product) => {
    if (product.is_direct_code) {
      setProductResults([]);
      setProductSearch('');
      setDirectOrderProduct(product);
      return;
    }
    setValidatingProduct(true);
    setProductResults([]);
    setProductSearch('');
    
    try {
      // Validate the product
      const validateResponse = await axios.post(`${API}/purchase-orders/validate-item`, {
        product_id: product.id,
        quantity: 1
      });
      
      const validationData = validateResponse.data;
      
      // Show product modal
      setSelectedProduct({
        ...validationData.product,
        stock_info: validationData.stock_info,
        case_validation: validationData.case_validation
      });
      setProductQty('');
      setProductCost(validationData.product.unit_cost?.toString() || '0');
      setStockWarning(null);
      setCaseWarning(null);
      setShowProductModal(true);
      
      setTimeout(() => {
        if (qtyInputRef.current) {
          qtyInputRef.current.focus();
        }
      }, 100);
    } catch (error) {
      console.error("Error validating product:", error);
      toast.error("Failed to load product details");
    } finally {
      setValidatingProduct(false);
    }
  };

  const handleProductSearch = async (e) => {
    if (e.key === 'Enter' && productSearch.trim()) {
      e.preventDefault();
      
      // If there are search results, select the first one
      if (productResults.length > 0) {
        handleSelectProduct(productResults[0]);
        return;
      }
      
      setValidatingProduct(true);
      
      try {
        // Search for product
        const searchResponse = await axios.get(`${API}/products/search?q=${encodeURIComponent(productSearch)}`);
        const products = searchResponse.data;
        
        if (products.length === 0) {
          toast.error("Product not found");
          setValidatingProduct(false);
          return;
        }
        
        // If exact match (PLU or barcode), use first result
        let product = products[0];
        
        // Check if it's an exact PLU or barcode match
        const exactMatch = products.find(p => 
          p.plu_code === productSearch || 
          p.barcode === productSearch
        );
        if (exactMatch) {
          product = exactMatch;
        }
        if (product.is_direct_code) {
          setProductSearch('');
          setDirectOrderProduct(product);
          setValidatingProduct(false);
          return;
        }
        
        // Validate the product
        const validateResponse = await axios.post(`${API}/purchase-orders/validate-item`, {
          product_id: product.id,
          quantity: 1
        });
        
        const validationData = validateResponse.data;
        
        // Show product modal
        setSelectedProduct({
          ...validationData.product,
          stock_info: validationData.stock_info,
          case_validation: validationData.case_validation
        });
        setProductQty('');
        setProductCost(validationData.product.unit_cost?.toString() || '0');
        setStockWarning(null);
        setCaseWarning(null);
        setShowProductModal(true);
        setProductSearch('');
        
        setTimeout(() => {
          if (qtyInputRef.current) {
            qtyInputRef.current.focus();
          }
        }, 100);
        
      } catch (error) {
        console.error("Error searching product:", error);
        toast.error("Product not found");
      } finally {
        setValidatingProduct(false);
      }
    }
  };

  const handleQtyChange = async (qty) => {
    setProductQty(qty);
    
    if (selectedProduct && qty && parseFloat(qty) > 0) {
      const quantity = parseFloat(qty);
      const stockInfo = selectedProduct.stock_info;
      const caseSize = selectedProduct.case_size;
      
      // Check if exceeds max
      if (stockInfo.max_level > 0 && (stockInfo.current_stock + quantity) > stockInfo.max_level) {
        setStockWarning({
          type: 'exceeds_max',
          message: `Warning: Order quantity (${quantity}) + current stock (${stockInfo.current_stock}) = ${stockInfo.current_stock + quantity} exceeds maximum level (${stockInfo.max_level})`
        });
      } else {
        setStockWarning(null);
      }
      
      // Check case size
      if (caseSize && caseSize > 1 && quantity % caseSize !== 0) {
        const suggestedQty = Math.ceil(quantity / caseSize) * caseSize;
        setCaseWarning({
          message: `Case size is ${caseSize}. Quantity ${quantity} is not a full case.`,
          suggested: suggestedQty
        });
      } else {
        setCaseWarning(null);
      }
    } else {
      setStockWarning(null);
      setCaseWarning(null);
    }
  };

  const handleAddProduct = (e) => {
    if (e) e.preventDefault();
    
    if (!productQty || parseFloat(productQty) <= 0) {
      toast.error("Please enter a valid quantity");
      return;
    }
    
    // Check if product is blocked or discontinued (cannot order)
    if (selectedProduct.is_blocked) {
      toast.error(`Cannot order BLOCKED product: ${selectedProduct.name}`);
      return;
    }
    if (selectedProduct.is_discontinued) {
      toast.error(`Cannot order DISCONTINUED product: ${selectedProduct.name}`);
      return;
    }

    const qty = parseFloat(productQty);

    // Block if quantity doesn't match case size / minimum order qty
    const minQty = selectedProduct.case_size || selectedProduct.pack_size || 1;
    if (minQty > 1 && qty % minQty !== 0) {
      const suggestedQty = Math.ceil(qty / minQty) * minQty;
      toast.error(`Minimum order qty is ${minQty}. Please order in multiples of ${minQty}. Suggested: ${suggestedQty}`);
      setProductQty(String(suggestedQty));
      return;
    }
    
    const cost = parseFloat(productCost) || 0;
    
    // Check if product already exists in order
    const existingIndex = orderItems.findIndex(item => item.product_id === selectedProduct.id);
    
    if (existingIndex >= 0) {
      // Update existing item
      const updatedItems = [...orderItems];
      updatedItems[existingIndex].quantity_ordered += qty;
      updatedItems[existingIndex].unit_cost = cost;
      updatedItems[existingIndex].total_cost = updatedItems[existingIndex].quantity_ordered * cost;
      setOrderItems(updatedItems);
      toast.success(`Updated ${selectedProduct.name} quantity`);
    } else {
      // Add new item
      const newItem = {
        product_id: selectedProduct.id,
        plu_code: selectedProduct.plu_code,
        barcode: selectedProduct.barcode,
        supplier_plu: selectedProduct.supplier_plu || '',  // Include supplier's product code
        name: selectedProduct.name,
        description: selectedProduct.description || '',
        quantity_ordered: qty,
        unit_cost: cost,
        total_cost: qty * cost,
        current_stock: selectedProduct.stock_info?.current_stock || 0,
        min_level: selectedProduct.stock_info?.min_level || 0,
        reorder_level: selectedProduct.stock_info?.reorder_level || 0,
        max_level: selectedProduct.stock_info?.max_level || 0,
        exceeds_max: stockWarning?.type === 'exceeds_max',
        sold_per_kg: selectedProduct.sold_per_kg || false,
        vat_category: selectedProduct.vat_category || 'Standard 15%',
        transport_cost: selectedProduct.transport_cost || 0,
        case_size: selectedProduct.case_size || null,
        pack_size: selectedProduct.pack_size || null,
        unit: selectedProduct.unit || 'units',
        weight_kg: parseFloat(selectedProduct.weight_kg) || 0,
        six_month_sold: selectedProduct.six_month_sold || 0  // 6-month sales quantity
      };
      setOrderItems([...orderItems, newItem]);
      toast.success(`Added ${selectedProduct.name}`);
    }
    
    // Close modal and focus back on search
    setShowProductModal(false);
    setSelectedProduct(null);
    setStockWarning(null);
    setCaseWarning(null);
    
    setTimeout(() => {
      if (productSearchRef.current) {
        productSearchRef.current.focus();
      }
    }, 100);
  };

  const handleRemoveItem = (index) => {
    const newItems = [...orderItems];
    newItems.splice(index, 1);
    setOrderItems(newItems);
  };

  const handleAddDirectOrderLine = ({ description, qty, unit_cost, ticket }) => {
    const code = directOrderProduct;
    setDirectOrderProduct(null);
    setOrderItems(prev => [...prev, {
      product_id: code.id,
      plu_code: code.plu_code,
      barcode: '',
      supplier_plu: '',
      name: description,
      description,
      quantity_ordered: qty,
      unit_cost,
      total_cost: +(qty * unit_cost).toFixed(2),
      current_stock: code.current_stock || 0,
      min_level: 0, reorder_level: 0, max_level: 0,
      exceeds_max: false,
      sold_per_kg: false,
      vat_category: code.vat_category || 'Standard 15%',
      case_size: null, pack_size: null,
      unit: code.unit || 'units',
      six_month_sold: 0,
      is_direct_line: true,
      direct_line_id: `dl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      direct_code_name: code.name,
      co_ticket_id: ticket?.id || null,
      co_ticket_no: ticket?.ticket_no || null,
      co_customer_name: ticket?.customer_name || null,
    }]);
    toast.success(`Added ${description} (${code.plu_code})`);
    setTimeout(() => productSearchRef.current?.focus(), 100);
  };

  // Open Once-Off Product dialog (Direct Stock Order)
  const handleOpenOnceOff = () => {
    setOnceOffName('');
    setOnceOffQty('1');
    setOnceOffPrice('');
    setOnceOffAddToStock(false);
    setOnceOffDialogOpen(true);
  };

  // Add a once-off product to the Direct Stock Order
  const handleAddOnceOff = async () => {
    const name = onceOffName.trim();
    const qty = parseFloat(onceOffQty);
    const cost = parseFloat(onceOffPrice);

    if (!name) { toast.error('Please enter a description'); return; }
    if (!qty || qty <= 0) { toast.error('Please enter a valid quantity'); return; }
    if (isNaN(cost) || cost < 0) { toast.error('Please enter a valid price (excl VAT)'); return; }

    setSavingOnceOff(true);
    try {
      let productId = `once-off-${Date.now()}`;
      let pluCode = '';

      // If user opted to also add to Stock Master, create the product first
      if (onceOffAddToStock) {
        // Retry loop — if next-plu races with a concurrent add (or a stale
        // client fetched the same PLU), POST returns 400 "already used".
        // Fetch a fresh PLU and try again up to 3 times so the operator
        // doesn't have to see a scary error and re-click.
        let lastErr;
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const pluRes = await axios.get(`${API}/products/next-plu`);
            pluCode = pluRes.data?.next_plu || '';
            if (!pluCode) throw new Error('No PLU returned');

            const sellingPriceInclVat = +(cost * 1.15 * 1.30).toFixed(2);
            const newProduct = {
              plu_code: pluCode,
              barcode: '',
              barcodes: [],
              name,
              category: 'General',
              department: 'General',
              supplier: selectedSupplier?.name || '',
              supplier_id: selectedSupplier?.id || '',
              supplier_plu: '',
              current_stock: 0,
              minimum_level: 0,
              reorder_level: 0,
              maximum_level: 0,
              unit: 'units',
              cost_price: cost,
              last_cost_price_excl_vat: cost,
              average_cost_price_excl_vat: cost,
              selling_price_incl_vat: sellingPriceInclVat,
              markup_percent: 30,
              vat_category: 'Standard 15%',
              no_discount: false,
              track_expiry: false,
              auto_calculate_stock_levels: false,
              is_quick_access: false,
              notes: 'Created from Direct Stock Order (once-off item)',
            };
            const res = await axios.post(`${API}/products`, newProduct);
            productId = res.data?.id || productId;
            pluCode = res.data?.plu_code || pluCode;
            toast.success(`"${name}" added to Stock Master (PLU ${pluCode})`);
            lastErr = null;
            break;
          } catch (err) {
            lastErr = err;
            const detail = err?.response?.data?.detail || '';
            // Only retry on duplicate-PLU / duplicate-key. Everything else
            // is a real problem the operator needs to see.
            if (!/already used|duplicate/i.test(String(detail))) break;
          }
        }
        if (lastErr) {
          console.error('Add to Stock Master failed:', lastErr?.response?.data || lastErr);
          const detail = lastErr?.response?.data?.detail
            || lastErr?.response?.data?.message
            || (typeof lastErr?.response?.data === 'string' ? lastErr.response.data : null)
            || lastErr?.message
            || 'Unknown error';
          toast.error(`Failed to add to Stock Master: ${detail}`);
          setSavingOnceOff(false);
          return;
        }
      }

      // Add to PO order items
      const newItem = {
        product_id: productId,
        plu_code: pluCode || '',
        barcode: '',
        supplier_plu: '',
        name,
        description: name,
        quantity_ordered: qty,
        unit_cost: cost,
        total_cost: +(qty * cost).toFixed(2),
        current_stock: 0,
        min_level: 0,
        reorder_level: 0,
        max_level: 0,
        exceeds_max: false,
        sold_per_kg: false,
        vat_category: 'Standard 15%',
        case_size: null,
        pack_size: null,
        unit: 'units',
        six_month_sold: 0,
        is_once_off: !onceOffAddToStock
      };
      setOrderItems(prev => [...prev, newItem]);
      if (!onceOffAddToStock) toast.success(`Added once-off item: ${name}`);

      setOnceOffDialogOpen(false);
    } finally {
      setSavingOnceOff(false);
    }
  };

  const handleUpdateItemQty = (index, qty) => {
    const newItems = [...orderItems];
    const item = newItems[index];
    const newQty = parseFloat(qty) || 0;
    const minQty = item.case_size || item.pack_size || 1;
    
    if (minQty > 1 && newQty > 0 && newQty % minQty !== 0) {
      const suggestedQty = Math.ceil(newQty / minQty) * minQty;
      toast.error(`Min order qty for ${item.name} is ${minQty}. Rounded to ${suggestedQty}`);
      newItems[index].quantity_ordered = suggestedQty;
    } else {
      newItems[index].quantity_ordered = newQty;
    }
    newItems[index].total_cost = newItems[index].quantity_ordered * newItems[index].unit_cost;
    setOrderItems(newItems);
  };

  const calculateTotals = () => {
    const subtotal = orderItems.reduce((sum, item) => sum + item.total_cost, 0);
    // Calculate VAT only on items that are not zero-rated or exempt.
    // Check BOTH vat_category text AND numeric vat_rate (some products only have the rate).
    const vat = orderItems.reduce((sum, item) => {
      const vatCategory = (item.vat_category || 'Standard 15%').toString().toLowerCase();
      const rateRaw = item.vat_rate;
      const rateNum = rateRaw === null || rateRaw === undefined || rateRaw === '' ? null : parseFloat(rateRaw);
      const isZero =
        vatCategory.includes('zero') ||
        vatCategory.includes('exempt') ||
        vatCategory.includes('0%') ||
        (rateNum !== null && !isNaN(rateNum) && rateNum <= 0.0001);
      if (isZero) {
        return sum;
      }
      return sum + (item.total_cost * 0.15);
    }, 0);
    const total = subtotal + vat;
    return { subtotal, vat, total };
  };

  const handleCompleteOrder = async (overrideMinimum = false) => {
    // Ensure overrideMinimum is a boolean (prevent event object being passed)
    const shouldOverride = overrideMinimum === true;
    
    if (orderItems.length === 0) {
      toast.error("Please add at least one item to the order");
      return;
    }
    
    const totals = calculateTotals();
    
    // Check minimum order — weight-based or value-based
    const isWeightSupplier = !!selectedSupplier?.is_weight_supplier;
    const minOrderType = selectedSupplier?.minimum_order_type || 'per_order';

    if (isWeightSupplier) {
      const minWeight = selectedSupplier?.minimum_order_weight_kg || 0;
      const totalWeight = orderItems.reduce(
        (sum, it) => sum + ((parseFloat(it.weight_kg) || 0) * (parseFloat(it.quantity_ordered) || 0)),
        0
      );
      if (minWeight > 0 && totalWeight < minWeight && !shouldOverride) {
        const shortfall = minWeight - totalWeight;
        setMinOrderDetails({
          mode: 'weight',
          minimum: minWeight,
          current: totalWeight,
          shortfall,
          type: minOrderType,
          supplierName: selectedSupplier.name,
        });
        setShowMinOrderWarning(true);
        return;
      }
    } else {
      const minOrderValue = selectedSupplier?.minimum_order_value || 0;
      if (minOrderValue > 0 && totals.subtotal < minOrderValue && !shouldOverride) {
        const shortfall = minOrderValue - totals.subtotal;
        setMinOrderDetails({
          mode: 'value',
          minimum: minOrderValue,
          current: totals.subtotal,
          shortfall,
          type: minOrderType,
          supplierName: selectedSupplier.name,
        });
        setShowMinOrderWarning(true);
        return;
      }
    }
    
    setSavingOrder(true);
    try {
      const orderData = {
        supplier_id: selectedSupplier.id,
        order_type: orderType,
        order_method: orderMethod,
        items: orderItems.map(item => ({
          product_id: item.product_id,
          plu_code: item.plu_code,
          barcode: item.barcode,
          supplier_plu: item.supplier_plu || '',  // Include supplier's product code
          name: item.name,
          description: item.description,
          quantity_ordered: item.quantity_ordered,
          quantity_received: 0,
          unit_cost: item.unit_cost,
          total_cost: item.total_cost,
          current_stock: item.current_stock,
          min_level: item.min_level,
          reorder_level: item.reorder_level,
          max_level: item.max_level,
          exceeds_max: item.exceeds_max,
          is_once_off: item.is_once_off || undefined,
          vat_category: item.vat_category,
          is_direct_line: item.is_direct_line || undefined,
          direct_line_id: item.direct_line_id,
          direct_code_name: item.direct_code_name,
          co_ticket_id: item.co_ticket_id,
          co_ticket_no: item.co_ticket_no,
          co_customer_name: item.co_customer_name,
        })),
        subtotal: totals.subtotal,
        vat_amount: totals.vat,
        total: totals.total,
        notes: shouldOverride
          ? (selectedSupplier?.is_weight_supplier
              ? `Minimum order weight (${selectedSupplier?.minimum_order_weight_kg || 0} kg) overridden by user`
              : `Minimum order value (R${selectedSupplier?.minimum_order_value || 0}) overridden by user`)
          : '',
        minimum_overridden: shouldOverride,
        created_by: 'System'
      };
      
      const response = await saveWithRetry(() => axios.post(`${API}/purchase-orders`, orderData));
      setCreatedOrder(response.data);
      setCurrentScreen('complete');
      toast.success(`Order ${response.data.po_number} created successfully`);
    } catch (error) {
      console.error("Error creating order:", error);
      toast.error(errorMessage(error, "Failed to create order"));
    } finally {
      setSavingOrder(false);
    }
  };

  const handleSendEmail = async () => {
    if (!createdOrder) return;
    
    setSendingEmail(true);
    try {
      await axios.post(`${API}/purchase-orders/${createdOrder.id}/send-email`, {
        email_to: emailAddress
      });
      toast.success(`Order will be sent to ${emailAddress}`);
      setCreatedOrder({ ...createdOrder, status: 'SENT', email_sent: true });
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  // Email PO from list view
  const handleEmailPOFromList = async (po) => {
    setEmailPOTarget(po);
    setEmailToMyself(false);
    
    // Fetch supplier details to get email
    try {
      const response = await axios.get(`${API}/suppliers/${po.supplier_id}`);
      const supplier = response.data;
      
      setSupplierEmail(supplier.email || '');
      setSupplierAutoEmail(supplier.auto_email_orders || false);
      
      // Pre-fill with supplier email if available
      if (supplier.email) {
        setListEmailAddress(supplier.email);
      } else {
        setListEmailAddress('');
      }
    } catch (error) {
      console.error("Error fetching supplier:", error);
      setSupplierEmail('');
      setSupplierAutoEmail(false);
      setListEmailAddress('');
    }
    
    setShowEmailDialog(true);
  };

  const sendPOEmail = async () => {
    if (!emailPOTarget || !listEmailAddress) return;
    
    setEmailingPOId(emailPOTarget.id);
    try {
      await axios.post(`${API}/purchase-orders/${emailPOTarget.id}/send-email`, {
        email_to: listEmailAddress
      });
      toast.success(`Order ${emailPOTarget.po_number} sent to ${listEmailAddress}`);
      setShowEmailDialog(false);
      // Refresh the list to update status
      fetchPurchaseOrders();
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to send email");
    } finally {
      setEmailingPOId(null);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // WhatsApp Purchase Order
  const handleWhatsApp = () => {
    if (!createdOrder) return;
    
    // Get supplier phone number
    const supplierPhone = createdOrder.supplier?.phone || createdOrder.supplier?.alt_phone || selectedSupplier?.phone;
    
    if (!supplierPhone) {
      toast.error("No phone number found for this supplier. Please add a phone number first.");
      return;
    }
    
    // Clean the phone number (remove spaces, dashes, etc.)
    let cleanPhone = supplierPhone.replace(/[\s\-\(\)]/g, '');
    
    // Add country code if not present (assuming South Africa +27)
    if (cleanPhone.startsWith('0')) {
      cleanPhone = '27' + cleanPhone.substring(1);
    } else if (!cleanPhone.startsWith('+') && !cleanPhone.startsWith('27')) {
      cleanPhone = '27' + cleanPhone;
    }
    cleanPhone = cleanPhone.replace('+', '');
    
    // Build order summary message
    const orderDate = new Date(createdOrder.created_at).toLocaleDateString('en-ZA');
    const itemsList = createdOrder.items.slice(0, 10).map(item => 
      `• ${item.product_name} x ${item.quantity}`
    ).join('\n');
    const moreItems = createdOrder.items.length > 10 ? `\n... and ${createdOrder.items.length - 10} more items` : '';
    
    const message = `📦 *PURCHASE ORDER*
    
*Order #:* ${createdOrder.order_number}
*Date:* ${orderDate}
*From:* BOSVELD BOU & HARDEWARE

*Items:*
${itemsList}${moreItems}

*Total (excl VAT):* R ${createdOrder.total_excl_vat?.toFixed(2) || '0.00'}
*VAT:* R ${createdOrder.vat_amount?.toFixed(2) || '0.00'}
*Total (incl VAT):* R ${createdOrder.total_incl_vat?.toFixed(2) || '0.00'}

Please confirm receipt of this order.
_PDF document to follow_`;

    // First trigger print (to save as PDF)
    toast.info("Opening print dialog - Save as PDF, then attach to WhatsApp");
    window.print();
    
    // Open WhatsApp with pre-filled message
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
    
    // Small delay to allow print dialog to open first
    setTimeout(() => {
      window.open(whatsappUrl, '_blank');
    }, 500);
  };

  const handleNewOrder = () => {
    setCurrentScreen('setup');
    setOrderType('STANDARD');
    setOrderMethod('MANUAL');
    setSupplierSearch('');
    setSelectedSupplier(null);
    setOrderItems([]);
    setCreatedOrder(null);
    setViewMode('create');
  };

  const totals = calculateTotals();

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <ShoppingCart className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold">Stock Orders</h1>
            <p className="text-gray-500">Create and manage purchase orders</p>
          </div>
        </div>
        
        {viewMode === 'list' ? (
          <Button onClick={handleNewOrder} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" />
            New Purchase Order
          </Button>
        ) : (
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => setViewMode('list')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to List
            </Button>
            {/* Progress indicator */}
            <div className="flex items-center gap-2">
              <Badge variant={currentScreen === 'setup' ? 'default' : 'secondary'} className="px-3">
                1. Setup
              </Badge>
              <ArrowRight className="h-4 w-4 text-gray-400" />
              <Badge variant={currentScreen === 'items' ? 'default' : 'secondary'} className="px-3">
                2. Items
              </Badge>
              <ArrowRight className="h-4 w-4 text-gray-400" />
              <Badge variant={currentScreen === 'complete' ? 'default' : 'secondary'} className="px-3">
                3. Complete
              </Badge>
            </div>
          </div>
        )}
      </div>

      {/* List View */}
      {viewMode === 'list' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />
              Purchase Orders
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Search input — filters loaded purchase orders client-side. */}
            <div className="mb-4 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={poSearchQuery}
                onChange={(e) => setPoSearchQuery(e.target.value)}
                placeholder="Search by PO #, supplier, status, PLU, item name…"
                className="pl-9"
                data-testid="po-list-search-input"
              />
              {poSearchQuery && (
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                  onClick={() => setPoSearchQuery("")}
                  data-testid="po-list-search-clear"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {(() => {
              const q = poSearchQuery.trim().toLowerCase();
              const filteredPOs = !q
                ? purchaseOrders
                : purchaseOrders.filter((po) => {
                    const hay = [
                      po.po_number,
                      po.order_number,
                      po.supplier_name,
                      po.supplier_number,
                      po.status,
                      ...(po.items || []).flatMap((it) => [it.name, it.product_name, it.plu_code]),
                    ]
                      .filter(Boolean)
                      .join(" ")
                      .toLowerCase();
                    return hay.includes(q);
                  });
              return (
            <>
            {loadingOrders ? (
              <div className="text-center py-8">
                <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-gray-500 mt-2">Loading purchase orders...</p>
              </div>
            ) : purchaseOrders.length === 0 ? (
              <div className="text-center py-8">
                <Package className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p className="text-gray-500">No purchase orders found</p>
                <Button onClick={handleNewOrder} className="mt-4 bg-blue-600 hover:bg-blue-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Order
                </Button>
              </div>
            ) : filteredPOs.length === 0 ? (
              <div className="text-center py-8" data-testid="po-list-no-results">
                <Search className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p className="text-gray-500">No purchase orders match "{poSearchQuery}".</p>
                <Button variant="outline" onClick={() => setPoSearchQuery("")} className="mt-2">Clear search</Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>PO Number</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPOs.map((po) => (
                    <TableRow key={po.id}>
                      <TableCell className="font-mono font-semibold text-blue-600">
                        {po.po_number}
                      </TableCell>
                      <TableCell>
                        {new Date(po.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{po.supplier_name}</div>
                          <div className="text-xs text-gray-500">{po.supplier_number}</div>
                        </div>
                      </TableCell>
                      <TableCell>{po.items?.length || 0} items</TableCell>
                      <TableCell className="text-right font-semibold">
                        R {(po.total || 0).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(po.status)}`}>
                          {po.status || 'DRAFT'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleViewPO(po)}>
                            View
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleEmailPOFromList(po)}
                            disabled={emailingPOId === po.id}
                            title="Email this order"
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeletingPO(po)}
                            title="Delete this PO"
                            data-testid={`delete-po-btn-${po.id}`}
                            disabled={(po.status || '').toUpperCase() === 'RECEIVED' || (po.status || '').toUpperCase() === 'PARTIAL'}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            </>
            );
            })()}
          </CardContent>
        </Card>
      )}

      {/* PO Detail Dialog */}
      <Dialog open={showPODetail} onOpenChange={(open) => {
        setShowPODetail(open);
        if (!open) handleCancelEditPO();
      }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Purchase Order - {selectedPO?.po_number}</span>
              {selectedPO && !editingPO && !['RECEIVED', 'CANCELLED', 'EXPIRED'].includes(selectedPO.status) && (
                <Button variant="outline" size="sm" onClick={handleStartEditPO}>
                  <Edit2 className="h-4 w-4 mr-1" />
                  Edit Order
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedPO && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Supplier:</span>
                  <div className="font-semibold">{selectedPO.supplier_name}</div>
                </div>
                <div>
                  <span className="text-gray-500">Date:</span>
                  <div className="font-semibold">{new Date(selectedPO.created_at).toLocaleDateString()}</div>
                </div>
                <div>
                  <span className="text-gray-500">Status:</span>
                  <div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(selectedPO.status)}`}>
                      {selectedPO.status || 'DRAFT'}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Edit Mode: Add Product Search */}
              {editingPO && (
                <div className="border rounded-lg p-3 bg-blue-50">
                  <Label className="text-sm font-medium mb-2 block">Add Product to Order</Label>
                  <div className="relative">
                    <Input
                      placeholder="Search by name, PLU, or barcode..."
                      value={editProductSearch}
                      onChange={(e) => setEditProductSearch(e.target.value)}
                      className="bg-white"
                    />
                    {editProductResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {editProductResults.map((product) => (
                          <div
                            key={product.id}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center"
                            onClick={() => handleAddEditProduct(product)}
                          >
                            <div>
                              <div className="font-medium text-sm">{product.name}</div>
                              <div className="text-xs text-gray-500">
                                PLU: {product.plu_code} | Stock: {product.current_stock}
                              </div>
                            </div>
                            <div className="text-sm text-gray-600">
                              R {((product.last_cost_price_excl_vat || product.cost_price || 0) + (product.transport_cost || 0)).toFixed(2)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>PLU</TableHead>
                    <TableHead>Supplier PLU</TableHead>
                    <TableHead>Barcode</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    {editingPO && <TableHead className="w-10"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(editingPO ? editPOItems : selectedPO.items)?.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-mono text-sm">{item.plu_code}</TableCell>
                      <TableCell className="font-mono text-sm text-gray-600">{item.supplier_plu || '-'}</TableCell>
                      <TableCell className="font-mono text-sm text-gray-600">{item.barcode || '-'}</TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell className="text-right">
                        {editingPO ? (
                          <Input
                            type="number"
                            value={item.quantity || item.quantity_ordered || 0}
                            onChange={(e) => handleEditItemQty(index, e.target.value)}
                            className="w-20 text-right h-8"
                            min="1"
                          />
                        ) : (
                          item.quantity_ordered || item.quantity || 0
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {editingPO ? (
                          <Input
                            type="number"
                            step="0.01"
                            value={item.unit_cost || 0}
                            onChange={(e) => handleEditItemCost(index, e.target.value)}
                            className="w-24 text-right h-8"
                            min="0"
                          />
                        ) : (
                          `R ${(item.unit_cost || 0).toFixed(2)}`
                        )}
                      </TableCell>
                      <TableCell className="text-right font-semibold">R {(item.total_cost || 0).toFixed(2)}</TableCell>
                      {editingPO && (
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveEditItem(index)}
                            className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              <div className="flex justify-end text-lg font-bold border-t pt-4">
                <span className="mr-4">Total:</span>
                <span>
                  R {editingPO 
                    ? (editPOItems.reduce((sum, item) => sum + (item.total_cost || 0), 0) * 1.15).toFixed(2)
                    : (selectedPO.total || 0).toFixed(2)
                  }
                </span>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            {editingPO ? (
              <>
                <Button variant="outline" onClick={handleCancelEditPO} disabled={savingEdit}>
                  Cancel
                </Button>
                <Button onClick={handleSaveEditPO} disabled={savingEdit || editPOItems.length === 0}>
                  {savingEdit ? "Saving..." : "Save Changes"}
                </Button>
              </>
            ) : (
              <>
                {selectedPO?.status === 'EXPIRED' && (
                  <Button
                    onClick={handleReactivatePO}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="reactivate-po-btn"
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Reactivate Order
                  </Button>
                )}
                <Button variant="outline" onClick={() => setShowPODetail(false)}>Close</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email PO Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email Purchase Order
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="text-sm text-gray-600">
              Send <span className="font-semibold">{emailPOTarget?.po_number}</span> to:
            </div>
            
            {/* Quick select buttons */}
            <div className="flex gap-2">
              {supplierEmail && (
                <Button
                  type="button"
                  variant={listEmailAddress === supplierEmail ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setListEmailAddress(supplierEmail);
                    setEmailToMyself(false);
                  }}
                  className="flex-1"
                >
                  Supplier
                </Button>
              )}
              <Button
                type="button"
                variant={emailToMyself ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setListEmailAddress(myEmail);
                  setEmailToMyself(true);
                }}
                className="flex-1"
              >
                Email to Myself
              </Button>
            </div>
            
            <div className="space-y-2">
              <Label>Email Address</Label>
              <Input
                type="email"
                value={listEmailAddress}
                onChange={(e) => {
                  setListEmailAddress(e.target.value);
                  setEmailToMyself(false);
                }}
                placeholder="Enter email address"
              />
            </div>
            
            {supplierAutoEmail && (
              <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm">
                <div className="flex items-center gap-2 text-green-700">
                  <Check className="h-4 w-4" />
                  <span className="font-medium">Auto-email enabled for this supplier</span>
                </div>
                <p className="text-green-600 text-xs mt-1">
                  Orders will be emailed automatically when status is changed to "SENT"
                </p>
              </div>
            )}
            
            <div className="text-xs text-gray-500 border-t pt-3">
              <strong>Supplier:</strong> {emailPOTarget?.supplier_name}<br />
              <strong>Items:</strong> {emailPOTarget?.items?.length || 0} | <strong>Total:</strong> R {(emailPOTarget?.total || 0).toFixed(2)}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={sendPOEmail} 
              disabled={emailingPOId || !listEmailAddress}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {emailingPOId ? (
                <>Sending...</>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create View - Screen 1: Setup */}
      {viewMode === 'create' && currentScreen === 'setup' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />
              Order Setup
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Order Type */}
            <div>
              <Label className="text-base font-semibold">1. Order Type</Label>
              <p className="text-sm text-gray-500 mb-3">What type of order is this?</p>
              <div className="grid grid-cols-2 gap-4">
                {ORDER_TYPES.map(type => (
                  <div
                    key={type.value}
                    onClick={() => setOrderType(type.value)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      orderType === type.value 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {orderType === type.value && <Check className="h-5 w-5 text-blue-500" />}
                      <span className="font-medium">{type.label}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{type.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Order Method */}
            <div>
              <Label className="text-base font-semibold">2. Order Method</Label>
              <p className="text-sm text-gray-500 mb-3">How do you want to create this order?</p>
              <div className="grid grid-cols-2 gap-4">
                {ORDER_METHODS.map(method => (
                  <div
                    key={method.value}
                    onClick={() => setOrderMethod(method.value)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      orderMethod === method.value 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {orderMethod === method.value && <Check className="h-5 w-5 text-blue-500" />}
                      <span className="font-medium">{method.label}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{method.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Supplier Selection */}
            <div>
              <Label className="text-base font-semibold">3. Select Supplier</Label>
              <p className="text-sm text-gray-500 mb-3">Enter supplier number or search by name</p>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input
                  value={supplierSearch}
                  onChange={(e) => {
                    setSupplierSearch(e.target.value);
                    setSelectedSupplier(null);
                  }}
                  placeholder="Enter supplier number or name..."
                  className="pl-10 text-lg h-12"
                  autoCapitalize={false}
                />
                {searchingSupplier && (
                  <div className="absolute right-3 top-3">
                    <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                  </div>
                )}
              </div>
              
              {/* Supplier search results */}
              {supplierResults.length > 0 && !selectedSupplier && (
                <div className="mt-2 border rounded-lg divide-y max-h-60 overflow-auto">
                  {supplierResults.map(supplier => (
                    <div
                      key={supplier.id}
                      onClick={() => handleSelectSupplier(supplier)}
                      className="p-3 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                    >
                      <div>
                        <span className="font-mono font-semibold text-blue-600">{supplier.supplier_number}</span>
                        <span className="mx-2">-</span>
                        <span className="font-medium">{supplier.name}</span>
                      </div>
                      <span className="text-sm text-gray-500">{supplier.city}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Selected supplier display */}
              {selectedSupplier && (
                <div className="mt-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-green-600" />
                      <div>
                        <span className="font-semibold">{selectedSupplier.supplier_number}</span>
                        <span className="mx-2">-</span>
                        <span>{selectedSupplier.name}</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedSupplier(null);
                        setSupplierSearch('');
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  {selectedSupplier.phone && (
                    <p className="text-sm text-gray-600 mt-1">Tel: {selectedSupplier.phone}</p>
                  )}
                  {/* Minimum Order Indicator */}
                  {selectedSupplier.is_weight_supplier && selectedSupplier.minimum_order_weight_kg > 0 && (
                    <div className="mt-2 p-2 bg-amber-100 border border-amber-300 rounded text-sm" data-testid="supplier-min-weight-badge">
                      <div className="flex items-center gap-2 text-amber-800">
                        <AlertTriangle className="h-4 w-4" />
                        <span className="font-medium">
                          Minimum Order: {selectedSupplier.minimum_order_weight_kg.toFixed(2)} kg
                        </span>
                        <Badge variant="outline" className="text-xs bg-white">
                          {selectedSupplier.minimum_order_type === 'combined' ? 'Combined' : 'Per Order'}
                        </Badge>
                      </div>
                    </div>
                  )}
                  {!selectedSupplier.is_weight_supplier && selectedSupplier.minimum_order_value > 0 && (
                    <div className="mt-2 p-2 bg-orange-100 border border-orange-300 rounded text-sm">
                      <div className="flex items-center gap-2 text-orange-700">
                        <AlertTriangle className="h-4 w-4" />
                        <span className="font-medium">
                          Minimum Order: R {selectedSupplier.minimum_order_value.toFixed(2)}
                        </span>
                        <Badge variant="outline" className="text-xs bg-white">
                          {selectedSupplier.minimum_order_type === 'combined' ? 'Combined' : 'Per Order'}
                        </Badge>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Proceed Button */}
            <div className="pt-4 border-t flex justify-end">
              <Button 
                onClick={handleProceedToItems}
                disabled={!selectedSupplier}
                className="bg-blue-600 hover:bg-blue-700"
                size="lg"
              >
                Proceed to Items
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Screen 2: Items */}
      {viewMode === 'create' && currentScreen === 'items' && (
        <div className="space-y-4">
          {/* Order Summary Bar */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Badge variant="outline" className="bg-white">
                    {orderType === 'STANDARD' ? 'Standard Order' : 'Direct Order'}
                  </Badge>
                  <Badge variant="outline" className="bg-white">
                    {orderMethod === 'MANUAL' ? 'Manual' : 'Auto Suggest'}
                  </Badge>
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    <span className="font-semibold">{selectedSupplier?.supplier_number}</span>
                    <span>-</span>
                    <span>{selectedSupplier?.name}</span>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => setCurrentScreen('setup')}>
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back to Setup
                </Button>
              </div>
              
              {/* Minimum Order Progress */}
              {selectedSupplier?.is_weight_supplier && selectedSupplier?.minimum_order_weight_kg > 0 && (
                <div className="mt-3 pt-3 border-t border-blue-200" data-testid="weight-progress-block">
                  {(() => {
                    const minWeight = selectedSupplier.minimum_order_weight_kg;
                    const currentWeight = orderItems.reduce(
                      (sum, item) => sum + ((parseFloat(item.weight_kg) || 0) * (parseFloat(item.quantity_ordered) || 0)),
                      0
                    );
                    const progress = Math.min((currentWeight / minWeight) * 100, 100);
                    const isMet = currentWeight >= minWeight;

                    return (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className={isMet ? "text-green-700 font-medium" : "text-amber-700 font-medium"}>
                            {isMet ? (
                              <span className="flex items-center gap-1">
                                <Check className="h-4 w-4" />
                                Minimum Weight Met!
                              </span>
                            ) : (
                              <span className="flex items-center gap-1">
                                <AlertTriangle className="h-4 w-4" />
                                Minimum: {minWeight.toFixed(2)} kg ({selectedSupplier.minimum_order_type === 'combined' ? 'Combined' : 'Per Order'})
                              </span>
                            )}
                          </span>
                          <span className={isMet ? "text-green-600" : "text-gray-600"}>
                            Current: {currentWeight.toFixed(2)} kg ({progress.toFixed(0)}%)
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-300 ${isMet ? 'bg-green-500' : 'bg-amber-500'}`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        {!isMet && (
                          <p className="text-xs text-amber-600">
                            Add {(minWeight - currentWeight).toFixed(2)} kg more to meet the minimum
                          </p>
                        )}
                        {orderItems.some((it) => !(parseFloat(it.weight_kg) > 0)) && (
                          <p className="text-xs text-red-600">
                            ⚠ One or more items have no weight set. Add a Weight (kg) on the product to count it toward the total.
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
              {!selectedSupplier?.is_weight_supplier && selectedSupplier?.minimum_order_value > 0 && (
                <div className="mt-3 pt-3 border-t border-blue-200">
                  {(() => {
                    const minValue = selectedSupplier.minimum_order_value;
                    const currentTotal = orderItems.reduce((sum, item) => sum + item.total_cost, 0);
                    const progress = Math.min((currentTotal / minValue) * 100, 100);
                    const isMet = currentTotal >= minValue;
                    
                    return (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className={isMet ? "text-green-700 font-medium" : "text-orange-700 font-medium"}>
                            {isMet ? (
                              <span className="flex items-center gap-1">
                                <Check className="h-4 w-4" />
                                Minimum Order Met!
                              </span>
                            ) : (
                              <span className="flex items-center gap-1">
                                <AlertTriangle className="h-4 w-4" />
                                Minimum: R {minValue.toFixed(2)} ({selectedSupplier.minimum_order_type === 'combined' ? 'Combined' : 'Per Order'})
                              </span>
                            )}
                          </span>
                          <span className={isMet ? "text-green-600" : "text-gray-600"}>
                            Current: R {currentTotal.toFixed(2)} ({progress.toFixed(0)}%)
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${isMet ? 'bg-green-500' : 'bg-orange-500'}`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        {!isMet && (
                          <p className="text-xs text-orange-600">
                            Add R {(minValue - currentTotal).toFixed(2)} more to meet the minimum
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Product Search (Manual mode + append in Auto mode) */}
          {(orderMethod === 'MANUAL' || orderMethod === 'AUTO') && (
            <Card>
              <CardContent className="py-4">
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-base font-semibold">
                    {orderMethod === 'AUTO' ? 'Add More Products (Optional)' : 'Add Products'}
                  </Label>
                  <div className="flex gap-2">
                    {orderType === 'DIRECT' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleOpenOnceOff}
                        className="text-purple-600 border-purple-300 hover:bg-purple-50"
                        data-testid="add-once-off-item-btn"
                      >
                        <PlusCircle className="h-4 w-4 mr-1" />
                        Add Once-Off Item
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleOpenAddProduct}
                      className="text-green-600 border-green-300 hover:bg-green-50"
                      data-testid="add-new-product-btn"
                    >
                      <PlusCircle className="h-4 w-4 mr-1" />
                      Add New Product
                    </Button>
                  </div>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <Input
                    ref={productSearchRef}
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    onKeyDown={handleProductSearch}
                    placeholder={orderMethod === 'AUTO'
                      ? "Append item by PLU, barcode, or product name..."
                      : "Enter PLU, barcode, or product name..."}
                    className="pl-10 text-lg h-12"
                    disabled={validatingProduct}
                    data-testid="po-product-search-input"
                  />
                  {(validatingProduct || searchingProducts) && (
                    <div className="absolute right-3 top-3">
                      <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                    </div>
                  )}
                </div>
                {orderMethod === 'AUTO' && (
                  <p className="text-xs text-gray-500 mt-2">
                    Manually appended items will be added to the auto-suggested list below.
                  </p>
                )}
                
                {/* Product search results dropdown */}
                {productResults.length > 0 && (
                  <div className="mt-2 border rounded-lg divide-y max-h-60 overflow-auto bg-white shadow-lg">
                    {productResults.map(product => (
                      <div
                        key={product.id}
                        onClick={() => handleSelectProduct(product)}
                        className="p-3 hover:bg-blue-50 cursor-pointer flex justify-between items-center"
                        data-testid={`po-product-result-${product.plu_code}`}
                      >
                        <div>
                          <span className="font-mono font-semibold text-blue-600 mr-2">{product.plu_code}</span>
                          <span className="font-medium">{product.name}</span>
                          {product.barcode && (
                            <span className="text-xs text-gray-400 ml-2">({product.barcode})</span>
                          )}
                        </div>
                        <div className="text-right text-sm">
                          <div className="text-gray-500">Stock: {product.current_stock || 0}</div>
                          <div className="font-medium">R {((product.last_cost_price_excl_vat || product.cost_price_excl_vat || 0) + (product.transport_cost || 0)).toFixed(2)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Auto Suggest Loading */}
          {orderMethod === 'AUTO' && loadingAutoSuggest && (
            <Card>
              <CardContent className="py-8 text-center">
                <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p>Loading suggested items based on reorder levels...</p>
              </CardContent>
            </Card>
          )}

          {/* Show All Supplier Items Toggle */}
          {orderMethod === 'AUTO' && !loadingAutoSuggest && (
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="showAllItems"
                      checked={showAllSupplierItems}
                      onChange={(e) => setShowAllSupplierItems(e.target.checked)}
                      className="h-4 w-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="showAllItems" className="text-sm font-medium text-blue-800 cursor-pointer">
                      Show ALL supplier stock items
                    </label>
                  </div>
                  <span className="text-xs text-blue-600">
                    {showAllSupplierItems 
                      ? "Showing all products from this supplier" 
                      : "Only showing items below reorder level"}
                  </span>
                </div>
                <div className="flex justify-end mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                    onClick={async () => {
                      try {
                        toast.info("Refreshing stock on order...");
                        const res = await axios.post(`${API}/products/refresh-stock-on-order`);
                        toast.success(res.data.message);
                      } catch { toast.error("Failed to refresh"); }
                    }}
                    data-testid="refresh-stock-on-order-btn"
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Refresh On-Order
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* No Allocation Warning */}
          {orderMethod === 'AUTO' && !loadingAutoSuggest && noAllocationWarning && (
            <Card className="border-amber-300 bg-amber-50">
              <CardContent className="py-6">
                <div className="flex items-start gap-4">
                  <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-amber-800 mb-2">No Products to Order</h4>
                    <p className="text-amber-700 mb-4">{noAllocationWarning}</p>
                    {noAllocationWarning.includes('Stock Allocation') && (
                      <Button 
                        variant="outline" 
                        className="border-amber-400 text-amber-700 hover:bg-amber-100"
                        onClick={() => window.location.href = '/supplier-allocation'}
                      >
                        <Building2 className="h-4 w-4 mr-2" />
                        Go to Stock Allocation
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Order Items Table */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Order Items ({orderItems.length})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {orderItems.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No items added yet</p>
                  {(orderMethod === 'MANUAL' || orderMethod === 'AUTO') && (
                    <p className="text-sm">Search for products above to add them to the order</p>
                  )}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-[80px]">PLU</TableHead>
                      <TableHead className="w-[100px]">Supplier PLU</TableHead>
                      <TableHead className="w-[120px]">Barcode</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right w-[80px]">Stock</TableHead>
                      <TableHead className="text-right w-[80px]">6M Sold</TableHead>
                      <TableHead className="text-right w-[120px]">Order Qty</TableHead>
                      <TableHead className="text-right w-[100px]">Unit Cost</TableHead>
                      <TableHead className="text-right w-[100px]">Total</TableHead>
                      <TableHead className="w-[60px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[...orderItems]
                      .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
                      .map((item, index) => {
                      // Check case size validation
                      const hasInvalidCase = item.case_size && item.case_size > 1 && item.quantity_ordered > 0 && item.quantity_ordered % item.case_size !== 0;
                      return (
                      <TableRow key={item.product_id || index} className={`${item.exceeds_max ? 'bg-amber-50' : ''} ${hasInvalidCase ? 'bg-orange-50' : ''}`}>
                        <TableCell className="font-mono">{item.plu_code}</TableCell>
                        <TableCell className="font-mono text-gray-600">{item.supplier_plu || '-'}</TableCell>
                        <TableCell className="font-mono text-gray-600">{item.barcode || '-'}</TableCell>
                        <TableCell>
                          <button
                            type="button"
                            onClick={() => openEditProduct(item.product_id || item.plu_code)}
                            disabled={loadingEditProduct}
                            className="font-medium text-left hover:text-indigo-700 hover:underline underline-offset-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 rounded"
                            title="Open product to edit cost, name, VAT category, case size, etc."
                            data-testid={`po-line-edit-product-${item.plu_code}`}
                          >
                            {item.name}
                          </button>
                          <span className="ml-1.5 align-middle">
                            <OrderImportanceBadge level={importanceMap[item.product_id]} compact testId={`po-line-importance-${item.plu_code}`} />
                          </span>
                          {item.is_direct_line && (
                            <div className="text-[11px] text-purple-700" data-testid={`po-line-direct-${index}`}>
                              {item.plu_code} {item.direct_code_name}{item.co_ticket_no ? ` · ${item.co_ticket_no} ${item.co_customer_name || ''}` : ''}
                            </div>
                          )}
                          {item.exceeds_max && (
                            <div className="flex items-center gap-1 text-amber-600 text-xs mt-1">
                              <AlertTriangle className="h-3 w-3" />
                              Exceeds max level
                            </div>
                          )}
                          {item.covered_by_order && (
                            <div className="flex items-center gap-1 text-blue-600 text-xs mt-1">
                              <Package className="h-3 w-3" />
                              {item.stock_on_order} already on order (covers max)
                            </div>
                          )}
                          {item.stock_on_order > 0 && !item.covered_by_order && (
                            <div className="flex items-center gap-1 text-blue-500 text-xs mt-1">
                              <Package className="h-3 w-3" />
                              {item.stock_on_order} on order
                            </div>
                          )}
                          {item.case_size && item.case_size > 1 && (
                            <div className="text-xs text-gray-500">
                              Case: {item.case_size} {item.unit || 'units'}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{item.current_stock}</TableCell>
                        <TableCell className="text-right text-blue-600 font-medium">
                          {item.six_month_sold || 0}
                        </TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number"
                            value={item.sold_per_kg ? item.quantity_ordered.toFixed(3) : item.quantity_ordered}
                            onChange={(e) => handleUpdateItemQty(orderItems.findIndex(o => o.product_id === item.product_id), e.target.value)}
                            className={`w-24 text-right ml-auto ${hasInvalidCase ? 'border-orange-400' : ''}`}
                            min={item.sold_per_kg ? "0.001" : "0"}
                            step={item.sold_per_kg ? "0.001" : "1"}
                          />
                          {item.sold_per_kg && <span className="text-xs text-blue-600 ml-1">kg</span>}
                          {hasInvalidCase && (
                            <div className="text-xs text-orange-600 mt-0.5">Not full case</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div>R {item.unit_cost.toFixed(2)}</div>
                          {(item.transport_cost || 0) > 0 && (
                            <div className="text-[10px] text-blue-600 leading-tight">
                              incl. R{(item.transport_cost || 0).toFixed(2)} transport
                            </div>
                          )}
                          {(() => {
                            const cat = (item.vat_category || '').toString().toLowerCase();
                            const rate = item.vat_rate === null || item.vat_rate === undefined || item.vat_rate === '' ? null : parseFloat(item.vat_rate);
                            const isZero = cat.includes('zero') || cat.includes('exempt') || cat.includes('0%') || (rate !== null && !isNaN(rate) && rate <= 0.0001);
                            return isZero ? (
                              <div className="text-[10px] text-green-600 leading-tight">0% VAT</div>
                            ) : null;
                          })()}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          R {item.total_cost.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveItem(orderItems.findIndex(o => o.product_id === item.product_id))}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Totals */}
          {orderItems.length > 0 && (
            <Card>
              <CardContent className="py-4">
                <div className="flex justify-between items-end">
                  <div className="text-sm text-gray-500">
                    {orderItems.length} item(s) in order
                  </div>
                  <div className="text-right space-y-1">
                    <div className="flex justify-between gap-8">
                      <span className="text-gray-500">Subtotal (Excl VAT):</span>
                      <span>R {totals.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between gap-8">
                      <span className="text-gray-500">VAT (on applicable items):</span>
                      <span>R {totals.vat.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between gap-8 text-lg font-bold border-t pt-1">
                      <span>Total:</span>
                      <span>R {totals.total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Complete Order Button */}
          <div className="flex justify-end">
            <Button
              onClick={() => handleCompleteOrder(false)}
              disabled={orderItems.length === 0 || savingOrder}
              className="bg-green-600 hover:bg-green-700"
              size="lg"
            >
              {savingOrder ? (
                <>
                  <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                  Creating Order...
                </>
              ) : (
                <>
                  <Check className="h-5 w-5 mr-2" />
                  Order Complete
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Screen 3: Complete */}
      {viewMode === 'create' && currentScreen === 'complete' && createdOrder && (
        <div className="space-y-4">
          {/* Success Message */}
          <Card className="bg-green-50 border-green-200">
            <CardContent className="py-6 text-center">
              <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-green-800 mb-2">Order Created Successfully!</h2>
              <p className="text-green-700">
                Order Number: <span className="font-mono font-bold text-xl">{createdOrder.po_number}</span>
              </p>
            </CardContent>
          </Card>

          {/* Order Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Order Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <Label className="text-gray-500">Order Number</Label>
                  <p className="font-mono font-bold text-lg">{createdOrder.po_number}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Status</Label>
                  <p>
                    <Badge className={createdOrder.status === 'SENT' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100'}>
                      {createdOrder.status}
                    </Badge>
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500">Supplier</Label>
                  <p className="font-medium">{createdOrder.supplier_number} - {createdOrder.supplier_name}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Order Type</Label>
                  <p>{createdOrder.order_type === 'STANDARD' ? 'Standard Stock Order' : 'Direct Stock Order'}</p>
                </div>
                {createdOrder.our_account_number && (
                  <div>
                    <Label className="text-gray-500">Our Account #</Label>
                    <p className="font-medium">{createdOrder.our_account_number}</p>
                  </div>
                )}
                <div>
                  <Label className="text-gray-500">Total Items</Label>
                  <p>{createdOrder.items.length}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Total Value</Label>
                  <p className="font-bold text-lg">R {createdOrder.total.toFixed(2)}</p>
                </div>
              </div>

              {/* Items Summary */}
              <div className="border-t pt-4">
                <Label className="text-base font-semibold mb-3 block">Order Items</Label>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Barcode</TableHead>
                      <TableHead>Supplier PLU</TableHead>
                      <TableHead>Our Code</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Unit Cost</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {createdOrder.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-mono text-xs">{item.barcode || '-'}</TableCell>
                        <TableCell className="font-mono text-xs">{item.supplier_plu || '-'}</TableCell>
                        <TableCell className="font-mono text-xs">{item.plu_code}</TableCell>
                        <TableCell>{item.name}</TableCell>
                        <TableCell className="text-right">{item.quantity_ordered}</TableCell>
                        <TableCell className="text-right">R {item.unit_cost.toFixed(2)}</TableCell>
                        <TableCell className="text-right">R {item.total_cost.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Send Order</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 flex-wrap">
                {/* Print Button */}
                <Button variant="outline" onClick={handlePrint}>
                  <Printer className="h-4 w-4 mr-2" />
                  Print Order (A4)
                </Button>

                {/* WhatsApp Button */}
                <Button 
                  variant="outline" 
                  onClick={handleWhatsApp}
                  className="bg-green-50 border-green-500 text-green-700 hover:bg-green-100"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  WhatsApp Order
                </Button>

                {/* Email Section */}
                <div className="flex-1 flex items-center gap-2">
                  <Label className="whitespace-nowrap">Email to:</Label>
                  <Input
                    type="email"
                    value={emailAddress}
                    onChange={(e) => setEmailAddress(e.target.value)}
                    className="max-w-xs"
                  />
                  <Button 
                    onClick={handleSendEmail}
                    disabled={sendingEmail || createdOrder.email_sent}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {sendingEmail ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                        Sending...
                      </>
                    ) : createdOrder.email_sent ? (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Email Sent
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4 mr-2" />
                        Send Email
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              {/* WhatsApp Help Text */}
              <p className="text-xs text-gray-500 mt-3">
                <MessageCircle className="h-3 w-3 inline mr-1" />
                WhatsApp: Opens print dialog first (save as PDF), then opens WhatsApp with order summary
              </p>
              
              {createdOrder.email_sent && (
                <p className="text-sm text-green-600 mt-2">
                  <Check className="h-4 w-4 inline mr-1" />
                  Order sent to {createdOrder.email_sent_to || emailAddress}
                </p>
              )}
            </CardContent>
          </Card>

          {/* New Order Button */}
          <div className="flex justify-center pt-4">
            <Button onClick={handleNewOrder} variant="outline" size="lg">
              <Plus className="h-5 w-5 mr-2" />
              Create New Order
            </Button>
          </div>
        </div>
      )}

      {/* Product Add Modal */}
      <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Product to Order</DialogTitle>
          </DialogHeader>

          {selectedProduct && (
            <form onSubmit={handleAddProduct} className="space-y-4">
              {/* Product Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="font-mono text-sm text-gray-500">{selectedProduct.plu_code}</div>
                <div className="font-semibold text-lg">{selectedProduct.name}</div>
                {selectedProduct.barcode && (
                  <div className="text-sm text-gray-500">Barcode: {selectedProduct.barcode}</div>
                )}
              </div>

              {/* Stock Info */}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-blue-50 p-2 rounded">
                  <div className="text-gray-500">Current Stock</div>
                  <div className="font-semibold">{selectedProduct.stock_info?.current_stock || 0}</div>
                </div>
                <div className="bg-gray-50 p-2 rounded">
                  <div className="text-gray-500">Max Level</div>
                  <div className="font-semibold">{selectedProduct.stock_info?.max_level || '-'}</div>
                </div>
              </div>

              {/* Quantity Input */}
              <div>
                <Label>Quantity to Order *</Label>
                <Input
                  ref={qtyInputRef}
                  type="number"
                  value={productQty}
                  onChange={(e) => handleQtyChange(e.target.value)}
                  placeholder="Enter quantity"
                  className="mt-1 text-lg"
                  min="1"
                  step="1"
                  autoFocus
                />
              </div>

              {/* Stock Warning */}
              {stockWarning && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-amber-800 text-sm">{stockWarning.message}</p>
                      <p className="text-amber-600 text-xs mt-1">You can still proceed with this order.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Case Size Warning */}
              {caseWarning && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
                    <div>
                      <p className="text-orange-800 text-sm">{caseWarning.message}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs border-orange-400 text-orange-700 hover:bg-orange-100"
                          onClick={() => {
                            setProductQty(caseWarning.suggested.toString());
                            setCaseWarning(null);
                          }}
                        >
                          Use {caseWarning.suggested} instead
                        </Button>
                        <span className="text-orange-600 text-xs">or proceed with partial case</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Case Size Info */}
              {selectedProduct.case_size && selectedProduct.case_size > 1 && !caseWarning && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-sm text-blue-700">
                  <Package className="h-4 w-4 inline mr-1" />
                  Case size: {selectedProduct.case_size} {selectedProduct.unit || 'units'} per case
                </div>
              )}

              {/* Cost Price */}
              <div>
                <Label>Unit Cost (Excl VAT)</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-2.5 text-gray-500">R</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={productCost}
                    onChange={(e) => setProductCost(e.target.value)}
                    className="pl-8"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Standard cost: R {selectedProduct.unit_cost?.toFixed(2) || '0.00'}
                </p>
              </div>

              {/* Line Total Preview */}
              {productQty && productCost && (
                <div className="bg-green-50 p-3 rounded-lg">
                  <div className="flex justify-between">
                    <span>Line Total:</span>
                    <span className="font-bold">R {(parseFloat(productQty) * parseFloat(productCost)).toFixed(2)}</span>
                  </div>
                  {selectedProduct.case_size && selectedProduct.case_size > 1 && (
                    <div className="text-xs text-gray-600 mt-1">
                      = {(parseFloat(productQty) / selectedProduct.case_size).toFixed(1)} cases
                    </div>
                  )}
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowProductModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Add to Order
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <DirectOrderLineDialog product={directOrderProduct} onClose={() => setDirectOrderProduct(null)} onAdd={handleAddDirectOrderLine} />

      {/* Once-Off Item Dialog (Direct Stock Order) */}
      <Dialog open={onceOffDialogOpen} onOpenChange={setOnceOffDialogOpen}>
        <DialogContent className="max-w-md" data-testid="once-off-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-purple-600" />
              Add Once-Off Item
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="mb-2 block">Description *</Label>
              <Input
                placeholder="e.g. Custom steel bracket, supplier-special item..."
                value={onceOffName}
                onChange={(e) => setOnceOffName(e.target.value)}
                autoFocus
                data-testid="once-off-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block">Quantity *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={onceOffQty}
                  onChange={(e) => setOnceOffQty(e.target.value)}
                  data-testid="once-off-qty-input"
                />
              </div>
              <div>
                <Label className="mb-2 block">Unit Cost (Excl VAT) *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={onceOffPrice}
                    onChange={(e) => setOnceOffPrice(e.target.value)}
                    className="pl-8"
                    data-testid="once-off-price-input"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
              <Checkbox
                id="onceOffAddToStock"
                checked={onceOffAddToStock}
                onCheckedChange={setOnceOffAddToStock}
                data-testid="once-off-add-to-stock-checkbox"
              />
              <Label htmlFor="onceOffAddToStock" className="text-sm cursor-pointer">
                Also add this item to Stock Master
                {selectedSupplier && (
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Will be linked to supplier: <strong>{selectedSupplier.name}</strong>
                  </span>
                )}
              </Label>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button
                variant="outline"
                onClick={() => setOnceOffDialogOpen(false)}
                disabled={savingOnceOff}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddOnceOff}
                disabled={savingOnceOff}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="once-off-add-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                {savingOnceOff ? 'Saving...' : 'Add to Order'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Add New Product Modal */}
      {newProductData && (
        <ProductDetailModal
          open={showAddProductModal}
          onClose={() => { setShowAddProductModal(false); setNewProductData(null); }}
          product={newProductData}
          onSave={handleSaveNewProduct}
          categories={categories}
          departments={departments}
          onRefreshLists={fetchCategoriesDepartments}
          isNew={true}
        />
      )}

      {/* Edit Existing Product Modal — opened from any Order Items row.
          Lets the operator fix a wrong cost / name / VAT / case size without
          leaving the order builder. Live-updates the order line on save. */}
      {editingProduct && (
        <ProductDetailModal
          open={showEditProductModal}
          onClose={() => { setShowEditProductModal(false); setEditingProduct(null); }}
          product={editingProduct}
          onSave={handleSaveEditedProduct}
          categories={categories}
          departments={departments}
          onRefreshLists={fetchCategoriesDepartments}
          isNew={false}
        />
      )}

      {/* Minimum Order Warning Dialog */}
      <Dialog open={showMinOrderWarning} onOpenChange={setShowMinOrderWarning}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="h-5 w-5" />
              {minOrderDetails?.mode === 'weight'
                ? 'Minimum Order Weight Not Met'
                : 'Minimum Order Value Not Met'}
            </DialogTitle>
          </DialogHeader>
          
          {minOrderDetails && (() => {
            const isWeight = minOrderDetails.mode === 'weight';
            const fmt = (v) => isWeight ? `${(v || 0).toFixed(2)} kg` : `R ${(v || 0).toFixed(2)}`;
            return (
            <div className="space-y-4">
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <p className="font-medium text-orange-800 mb-3">
                  {minOrderDetails.supplierName} requires a minimum order of:
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Minimum Required:</span>
                    <span className="font-bold text-orange-700">{fmt(minOrderDetails.minimum)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{isWeight ? 'Your Order Weight:' : 'Your Order Total:'}</span>
                    <span className="font-bold">{fmt(minOrderDetails.current)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-orange-200">
                    <span className="text-gray-600">Shortfall:</span>
                    <span className="font-bold text-red-600">{fmt(minOrderDetails.shortfall)}</span>
                  </div>
                </div>
              </div>
              
              <div className="p-3 bg-blue-50 rounded-lg text-sm">
                <p className="text-blue-800">
                  <strong>Order Type:</strong> {minOrderDetails.type === 'combined' 
                    ? "Combined - multiple orders can add up to meet the minimum"
                    : "Per Order - each order must meet the minimum"}
                </p>
              </div>
              
              <div className="flex gap-2 justify-end pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowMinOrderWarning(false)}
                >
                  Add More Items
                </Button>
                <Button 
                  variant="destructive"
                  onClick={() => {
                    setShowMinOrderWarning(false);
                    handleCompleteOrder(true); // Override minimum
                  }}
                >
                  Override & Create Order
                </Button>
              </div>
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Delete PO confirmation */}
      <AlertDialog open={!!deletingPO} onOpenChange={(o) => !o && setDeletingPO(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this Purchase Order?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingPO?.po_number} — {deletingPO?.supplier_name} — R {(deletingPO?.total || 0).toFixed(2)}
              <br />
              <br />
              This will permanently remove the PO and roll back any reserved
              stock-on-order. It cannot be undone. (Tip: PARTIAL or RECEIVED
              POs cannot be deleted — reverse the GRV first.)
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteInFlight}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePO}
              disabled={deleteInFlight}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-po-btn"
            >
              {deleteInFlight ? "Deleting…" : "Delete PO"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PurchaseOrders;
